/* ==========================================================================
   FILE: global/hooks/useMediaQuery.js
   IAMONEAI - Media Query Hooks
   Responsive breakpoint detection and media query utilities
   ========================================================================== */

import { useState, useEffect, useCallback } from 'react';

// ==========================================================================
// BREAKPOINT DEFINITIONS
// ==========================================================================

export const BREAKPOINTS = {
  xs: 0,
  sm: 375,
  md: 640,
  lg: 768,
  xl: 1024,
  '2xl': 1280,
};

export const BREAKPOINT_QUERIES = {
  xs: '(max-width: 374px)',
  sm: '(min-width: 375px) and (max-width: 639px)',
  md: '(min-width: 640px) and (max-width: 767px)',
  lg: '(min-width: 768px) and (max-width: 1023px)',
  xl: '(min-width: 1024px) and (max-width: 1279px)',
  '2xl': '(min-width: 1280px)',
  
  // Convenience queries
  mobile: '(max-width: 639px)',
  tablet: '(min-width: 640px) and (max-width: 1023px)',
  desktop: '(min-width: 1024px)',
  
  // Orientation
  portrait: '(orientation: portrait)',
  landscape: '(orientation: landscape)',
  
  // Device capabilities
  touch: '(hover: none) and (pointer: coarse)',
  mouse: '(hover: hover) and (pointer: fine)',
  
  // User preferences
  darkMode: '(prefers-color-scheme: dark)',
  lightMode: '(prefers-color-scheme: light)',
  reducedMotion: '(prefers-reduced-motion: reduce)',
  highContrast: '(prefers-contrast: high)',
};

// ==========================================================================
// CORE MEDIA QUERY HOOK
// ==========================================================================

/**
 * Hook to detect if a media query matches
 * @param {string} query - CSS media query string
 * @returns {boolean} Whether the media query matches
 * 
 * @example
 * const isMobile = useMediaQuery('(max-width: 639px)');
 * const isDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
 */
export function useMediaQuery(query) {
  // Handle SSR
  const getMatches = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  }, [query]);

  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);
    
    // Set initial value
    setMatches(mediaQuery.matches);

    // Handler for changes
    const handler = (event) => {
      setMatches(event.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
    
    // Legacy browsers
    mediaQuery.addListener(handler);
    return () => mediaQuery.removeListener(handler);
  }, [query]);

  return matches;
}

// ==========================================================================
// BREAKPOINT HOOK
// ==========================================================================

/**
 * Hook to get current breakpoint and responsive helpers
 * @returns {Object} Breakpoint state and helpers
 * 
 * @example
 * const { isMobile, isTablet, isDesktop, breakpoint } = useBreakpoint();
 */
export function useBreakpoint() {
  const isXs = useMediaQuery(BREAKPOINT_QUERIES.xs);
  const isSm = useMediaQuery(BREAKPOINT_QUERIES.sm);
  const isMd = useMediaQuery(BREAKPOINT_QUERIES.md);
  const isLg = useMediaQuery(BREAKPOINT_QUERIES.lg);
  const isXl = useMediaQuery(BREAKPOINT_QUERIES.xl);
  const is2xl = useMediaQuery(BREAKPOINT_QUERIES['2xl']);
  
  // Convenience flags
  const isMobile = useMediaQuery(BREAKPOINT_QUERIES.mobile);
  const isTablet = useMediaQuery(BREAKPOINT_QUERIES.tablet);
  const isDesktop = useMediaQuery(BREAKPOINT_QUERIES.desktop);

  // Get current breakpoint name
  const breakpoint = (() => {
    if (is2xl) return '2xl';
    if (isXl) return 'xl';
    if (isLg) return 'lg';
    if (isMd) return 'md';
    if (isSm) return 'sm';
    return 'xs';
  })();

  // Get breakpoint value in pixels
  const breakpointValue = BREAKPOINTS[breakpoint] || 0;

  return {
    // Exact breakpoint matches
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,
    is2xl,
    
    // Convenience flags
    isMobile,
    isTablet,
    isDesktop,
    
    // Current breakpoint
    breakpoint,
    breakpointValue,
    
    // Min-width checks (is at least this size)
    isAtLeastSm: !isXs,
    isAtLeastMd: !isXs && !isSm,
    isAtLeastLg: isLg || isXl || is2xl,
    isAtLeastXl: isXl || is2xl,
    isAtLeast2xl: is2xl,
  };
}

// ==========================================================================
// DEVICE CAPABILITY HOOKS
// ==========================================================================

/**
 * Hook to detect if device is touch-capable
 * @returns {boolean} Whether device has touch capability
 */
export function useTouchDevice() {
  const isTouch = useMediaQuery(BREAKPOINT_QUERIES.touch);
  
  // Additional check for touch support
  const [hasTouch, setHasTouch] = useState(false);
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const touchSupport = 
      'ontouchstart' in window || 
      navigator.maxTouchPoints > 0;
    
    setHasTouch(touchSupport);
  }, []);
  
  return isTouch || hasTouch;
}

/**
 * Hook to detect device orientation
 * @returns {Object} Orientation state
 */
export function useOrientation() {
  const isPortrait = useMediaQuery(BREAKPOINT_QUERIES.portrait);
  const isLandscape = useMediaQuery(BREAKPOINT_QUERIES.landscape);
  
  return {
    isPortrait,
    isLandscape,
    orientation: isPortrait ? 'portrait' : 'landscape',
  };
}

/**
 * Hook to detect user preferences
 * @returns {Object} User preference state
 */
export function useUserPreferences() {
  const prefersDarkMode = useMediaQuery(BREAKPOINT_QUERIES.darkMode);
  const prefersLightMode = useMediaQuery(BREAKPOINT_QUERIES.lightMode);
  const prefersReducedMotion = useMediaQuery(BREAKPOINT_QUERIES.reducedMotion);
  const prefersHighContrast = useMediaQuery(BREAKPOINT_QUERIES.highContrast);
  
  return {
    prefersDarkMode,
    prefersLightMode,
    prefersReducedMotion,
    prefersHighContrast,
    colorScheme: prefersDarkMode ? 'dark' : 'light',
  };
}

// ==========================================================================
// WINDOW SIZE HOOK
// ==========================================================================

/**
 * Hook to get current window dimensions
 * @returns {Object} Window width and height
 * 
 * @example
 * const { width, height } = useWindowSize();
 */
export function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Debounce resize events
    let timeoutId;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', debouncedResize);
    
    // Set initial size
    handleResize();

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  return windowSize;
}

// ==========================================================================
// SCROLL POSITION HOOK
// ==========================================================================

/**
 * Hook to track scroll position
 * @returns {Object} Scroll position and direction
 */
export function useScrollPosition() {
  const [scrollPosition, setScrollPosition] = useState({
    x: 0,
    y: 0,
    direction: null,
    isAtTop: true,
    isAtBottom: false,
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateScrollPosition = () => {
      const currentScrollY = window.scrollY;
      const currentScrollX = window.scrollX;
      
      const direction = currentScrollY > lastScrollY ? 'down' : 'up';
      const isAtTop = currentScrollY <= 0;
      const isAtBottom = 
        window.innerHeight + currentScrollY >= 
        document.documentElement.scrollHeight - 10;

      setScrollPosition({
        x: currentScrollX,
        y: currentScrollY,
        direction,
        isAtTop,
        isAtBottom,
      });

      lastScrollY = currentScrollY;
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollPosition);
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Set initial position
    updateScrollPosition();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return scrollPosition;
}

// ==========================================================================
// VIEWPORT VISIBILITY HOOK
// ==========================================================================

/**
 * Hook to detect if element is in viewport
 * @param {React.RefObject} ref - Ref to the element to observe
 * @param {Object} options - IntersectionObserver options
 * @returns {boolean} Whether element is visible in viewport
 * 
 * @example
 * const ref = useRef();
 * const isVisible = useInViewport(ref, { threshold: 0.5 });
 */
export function useInViewport(ref, options = {}) {
  const [isInViewport, setIsInViewport] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback for older browsers
      setIsInViewport(true);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      setIsInViewport(entry.isIntersecting);
    }, {
      threshold: 0,
      rootMargin: '0px',
      ...options,
    });

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [ref, options.threshold, options.rootMargin, options.root]);

  return isInViewport;
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default {
  useMediaQuery,
  useBreakpoint,
  useTouchDevice,
  useOrientation,
  useUserPreferences,
  useWindowSize,
  useScrollPosition,
  useInViewport,
  BREAKPOINTS,
  BREAKPOINT_QUERIES,
};